import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { NARROW_BREAKPOINT_PX } from './use-narrow';
import { ACTIVITY_MAP_HIDE_TABS_SPEC, ACTIVITY_MAP_HOME_SPEC, AGENTS_WORLD_HOME_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Which home this account opens on.
 *
 * Three homes, each behind its OWN feature release, and the sidebar dropdown that used to choose
 * between them is gone. A home is no longer something the reader picks and we remember — it is the one
 * the rollout put them on, so a cohort can be moved without asking anybody to change a setting.
 *
 * This replaces `symphonyHome2Web` + the `symphony.homepage` localStorage preference. That pairing had
 * one arm opening a menu of three, which meant the rollout could only ever say "new homes, yes or no"
 * and never "this cohort gets THIS home" — and two people in the same cohort saw different screens.
 *
 * Shared 1:1 with the phones' `HomeVariant`.
 */
export type HomeVariant =
  /**
   * What every user sees today: the catch-up home. The fallback, and the only one with no release of
   * its own — it is what you get when no other home is on, so a release for it would be a switch that
   * must never be turned off.
   */
  | 'today'
  /** The free-camera agents world (`MapHome view="agents-world"`). */
  | 'agents-world'
  /**
   * The Overview screen (`Home3Body`) — the task list, with its own header switch to the activity map
   * hosted inside it. The map is a TAB of this home, not a home of its own.
   */
  | 'activity-map';

/**
 * The home to open, given which releases this account is in. Pure, so the phones' twin and the tests
 * read the same rule.
 *
 * Both can be on at once — they are independent rollouts, and nothing stops an account landing in both
 * cohorts — so the order is a decision, not an accident: the NEWEST home wins. A reader moved onto the
 * activity map should see it even if they were left in the agents-world cohort, otherwise the newer
 * rollout would silently do nothing for them.
 *
 * Falls back to `today`: with nothing resolved (a gate that timed out, an unstarted release, an
 * error), the reader gets the home that has always worked rather than a half-ramped one.
 */
export function resolveHomeVariant(activityMap: boolean, agentsWorld: boolean): HomeVariant {
  if (activityMap) return 'activity-map';
  if (agentsWorld) return 'agents-world';
  return 'today';
}

/**
 * Whether this home already IS the agents screen, so the rail's own "Your agents" entry would be a
 * second door to it.
 *
 * Only the agents WORLD does. The activity map draws agents as dots on a ring — a summary — and its
 * reader may be sitting on the task-list tab and not looking at the map at all, so taking the entry
 * away would leave them no way in.
 */
export function homeShowsAgentsItself(variant: HomeVariant): boolean {
  return variant === 'agents-world';
}

/** Whether this home carries a map at all. The catch-up home does not. */
export function homeHasMap(variant: HomeVariant): boolean {
  return variant !== 'today';
}

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

const cached: Partial<Record<string, Promise<boolean>>> = {};

function gate(spec: string, route: string): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(spec);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  const hit = cached[spec];
  if (hit) return hit;
  const promise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/${route}`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  cached[spec] = promise;
  return promise;
}

export function agentsWorldHomeEnabled(): Promise<boolean> {
  return gate(AGENTS_WORLD_HOME_SPEC, 'agents-world-home');
}

export function activityMapHomeEnabled(): Promise<boolean> {
  return gate(ACTIVITY_MAP_HOME_SPEC, 'activity-map-home');
}

export function activityMapTabsHiddenEnabled(): Promise<boolean> {
  return gate(ACTIVITY_MAP_HIDE_TABS_SPEC, 'activity-map-hide-tabs');
}

/** The true viewport, read the way `useViewportNarrow` reads it. The hide-tabs arm is desktop-only. */
function viewportIsNarrow(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(`(max-width: ${NARROW_BREAKPOINT_PX - 1}px)`).matches
  );
}

/**
 * The hide-tabs answer the home resolved with, fixed for the page session. A conduct that missed the
 * home gates' deadline is recorded as `false`, and its late answer is dropped: the home has already
 * mounted with the switch, and hiding it then is the flash this gate exists to prevent.
 */
let tabsHiddenDecision: boolean | undefined;

/**
 * False while the task-list ⇄ map switch is still being built. Until it ships, the switch is hidden for
 * every reader — desktop and mobile web, whatever `ACTIVITY_MAP_HIDE_TABS_SPEC` says — so no user can
 * reach the unfinished map view. Flip to true when it ships. Mirrors the phones'
 * `ActivityHomeView.switchReady` / `SWITCH_READY`.
 */
export const VIEW_SWITCH_READY = false;

/**
 * Whether the activity-map home hides its task-list ⇄ map switch. `enabled` is the caller's desktop
 * check: a mobile-web reader is never conducted, since the arm cannot change their screen.
 *
 * On a desktop load `useHomeVariantGate` has already decided by the time this mounts. Only a reader
 * who loaded narrow and then widened the window gets here undecided, and is asked then.
 */
export function useActivityMapTabsHidden(enabled: boolean): boolean {
  const [hidden, setHidden] = useState(() => {
    const forced = toggleOverride(ACTIVITY_MAP_HIDE_TABS_SPEC);
    return forced !== undefined ? forced === 'true' : tabsHiddenDecision === true;
  });
  useEffect(() => {
    if (!enabled || tabsHiddenDecision !== undefined) return undefined;
    let live = true;
    void activityMapTabsHiddenEnabled().then((value) => {
      tabsHiddenDecision = value;
      if (live) setHidden(value);
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  return !VIEW_SWITCH_READY || (enabled && hidden);
}

/**
 * The resolved home, and whether both gates have answered.
 *
 * Starts on `today` — the control home — so a reader in neither cohort never sees another home flash
 * in and out. `resolved` is for a caller that must not act on that default (a page-view beacon that
 * would otherwise fire under the wrong page name before the gates land).
 */
export function useHomeVariantGate(): { variant: HomeVariant; resolved: boolean } {
  const [state, setState] = useState<{ variant: HomeVariant; resolved: boolean }>({
    variant: 'today',
    resolved: false,
  });
  useEffect(() => {
    let live = true;
    // The hide-tabs experiment is conducted only for a desktop reader the map home is on, so nobody else
    // is logged as exposed to it. It shares the home gates' deadline rather than adding its own, and a
    // miss reads as control (see `tabsHiddenDecision`).
    const deadline = Date.now() + GATE_TIMEOUT_MS;
    const map = activityMapHomeEnabled().then(async (on) => {
      if (on && !viewportIsNarrow() && tabsHiddenDecision === undefined) {
        const missed = new Promise<false>((resolve) => setTimeout(() => resolve(false), Math.max(0, deadline - Date.now())));
        tabsHiddenDecision = await Promise.race([activityMapTabsHiddenEnabled(), missed]);
      }
      return on;
    });
    void Promise.all([map, agentsWorldHomeEnabled()]).then(([map, world]) => {
      if (live) setState({ variant: resolveHomeVariant(map, world), resolved: true });
    });
    return () => {
      live = false;
    };
  }, []);
  return state;
}

export function useHomeVariant(): HomeVariant {
  return useHomeVariantGate().variant;
}
